import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Col, Container, Row, Button } from 'reactstrap';
import { useParams, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import { ActionIcon, Collapse, Checkbox } from '@mantine/core';
import { IconChevronLeft, IconChevronRight, IconStackMiddle, IconLayersSubtract, IconMap } from '@tabler/icons-react';
import { MapEstablishmentList } from './Widgets/MapEstablishmentList';
import Summary from './Widgets/Summary';
import LeftPanel from './Widgets/LeftPanel';
import RightPanel from './Widgets/RightPanel';
import { useFilters } from '../../../Context/FiltersContext';

import OxygenSaturationSummary from './../../Establishment/Establishments/Summary/Widgets/OxygenSaturationSummary';
import DissolvedOxygenSummary from '../../Establishment/Establishments/Summary/Widgets/DissolvedOxygenSummary';
import TemperatureSummary from '../../Establishment/Establishments/Summary/Widgets/TemperatureSummary';
import SalinitySummary from '../../Establishment/Establishments/Summary/Widgets/SalinitySummary';
import ChlorophyllSummary from '../../Establishment/Establishments/Summary/Widgets/ChlorophyllSummary';
import TurbiditySummary from '../../Establishment/Establishments/Summary/Widgets/TurbiditySummary';
import OPlaceholder from '../../../Components/OElements/OPlaceholder';
import { getEstablishment } from '../../../slices/thunks';
import { getInformationRILesCoords } from '../../../helpers/data_sped/backend_helper';
import { toSmartTitleCase } from "../../../utils/toSmartTitleCase";

const MAPBOX_STYLES = [
		{
			key: 'light',
			label: 'Light',
			icon: '/icons/light.webp',
			style: 'mapbox://styles/mapbox/light-v11'
		},
		{
			key: 'satellite',
			label: 'Satelite',
			icon: '/icons/satellite.webp',
			style: 'mapbox://styles/mapbox/satellite-streets-v12'
		},
		{
			key: 'streets',
			label: 'Streets',
			icon: '/icons/streets.webp',
			style: 'mapbox://styles/mapbox/streets-v12'
		}
	];

const LAYER_OPTIONS = [
    { value: 'riles', label: 'RILes' }
];

const DashboardVariableRILes = () => {
    const [searchParams, setSearchParams] = useSearchParams();

    const handleSelectEstablishment = useCallback((establishment) => {
        if (establishment === null) {
            setSearchParams({});
            setShowLeftPanel(false);
        } else {
            setSearchParams({
                code_siep: establishment.code_siep,
            });
            setShowLeftPanel(true);
            setShowLayerFilter(false);
            setLeftPanelSection("widgets");
        }
    }, [setSearchParams]);
    const [currentWidgetIndex, setCurrentWidgetIndex] = useState(0);
    const [showLeftPanel, setShowLeftPanel] = useState(false);
    const [leftPanelSection, setLeftPanelSection] = useState("widgets");
    const [activeTab, setActiveTab] = useState('graficos');
    const [selectedMapStyle, setSelectedMapStyle] = useState(MAPBOX_STYLES[0].style);
    const [showLayerFilter, setShowLayerFilter] = useState(false);
    const [selectedLayers, setSelectedLayers] = useState(LAYER_OPTIONS.map(l => l.value));
    const dispatch = useDispatch();

    const { establishments, setEstablishments, filteredEstablishments, mapEstablishments, isLoadingEstablishments, setIsLoadingEstablishments } = useFilters();
    const [allEstablishments, setAllEstablishments] = useState([]);

    const toggleLeftPanel = useCallback(() => {
        setShowLayerFilter(false);
        setLeftPanelSection(prev => prev === "widgets" ? null : "widgets");
    }, []);

    const toggleLeftStyleMap = useCallback(() => {
        setShowLayerFilter(false);
        setLeftPanelSection(prev => prev === "styles" ? null : "styles");
    }, []);

    const toggleLayerFilter = useCallback(() => {
        setLeftPanelSection(null);
        setShowLayerFilter(prev => !prev);
    }, []);

    const codeSiep = useMemo(() => searchParams.get('code_siep'), [searchParams]);

    const selectedEstablishment = useMemo(() => {
        if (!codeSiep || !establishments.length) return null;

        const found = establishments.find(establishment => {
            if (establishment.siep_code && establishment.siep_code.toString() === codeSiep) {
                return true;
            }
            const coordsPattern = `${establishment.latitud}_${establishment.longitud}`;
            return coordsPattern === codeSiep;
        });

        if (found) {
            return {
                _id: String(found._id || found.siep_code || 'unknown'),
                nombre_centro: String(found.planta || 'Sin nombre'),
                region_centro: String((typeof found.region === 'string' ? found.region : found.region?.name) || 'Región no especificada'),
                siep_code: String(found.siep_code || `${found.latitud}_${found.longitud}`),
            };
        }

        return null;
    }, [codeSiep, establishments]);

    const widgets = useMemo(() => [
        {
            component: OxygenSaturationSummary,
            title: 'Saturación Oxígeno',
            um: '%',
        },
        {
            component: DissolvedOxygenSummary,
            title: 'Oxígeno Disuelto',
            um: 'mg/L',
        },
        {
            component: TemperatureSummary,
            title: 'Temperatura',
            um: '°C',
        },
        {
            component: SalinitySummary,
            title: 'Salinidad',
            um: 'psu',

        }
    ], []);

    useEffect(() => {
        document.title = 'SPED - Dashboard Variable';
    }, []);

    useEffect(() => {
        const loadEstablishments = async () => {
            try {
                setIsLoadingEstablishments(true);

                const response = await getInformationRILesCoords({
                    page:1,
                    limit:300,
                });

                setEstablishments(response);
                setAllEstablishments(response);

            } catch (error) {
                console.error('Error loading establishments:', error);
            } finally {
                setIsLoadingEstablishments(false);
            }
        };
        loadEstablishments();
    }, [setEstablishments, setIsLoadingEstablishments]);


    useEffect(() => {
        if (selectedLayers.includes('riles')) {
            setEstablishments(allEstablishments);
        } else {
            setEstablishments([]);
        }
    }, [selectedLayers, allEstablishments, setEstablishments]);



    useEffect(() => {
        const code_siep = searchParams.get('code_siep');
        if (code_siep && code_siep !== 'all') {
            dispatch(getEstablishment(code_siep));
        }
    }, [searchParams, dispatch]);


    useEffect(() => {
        const code_siep = searchParams.get('code_siep');
        setShowLeftPanel(code_siep && code_siep !== 'all');
    }, [searchParams]);

    const actionButtonsComponent = (
        <section className='d-flex flex-column gap-3 button-action-show-left-panel'>
            <ActionIcon
                onClick={toggleLeftPanel}
                size="lg"
                radius="xl"
                variant="filled"
                color="primary"
                style={{
                    visibility: showLeftPanel ? 'visible' : 'hidden'
                }}
            >
                {leftPanelSection === "widgets" ?  <IconChevronLeft size={18}/> : <IconChevronRight size={18} /> }
            </ActionIcon>
            <ActionIcon
                onClick={toggleLeftStyleMap}
                size="lg"
                radius="xl"
                variant="filled"
                color="primary"
            >
                <IconMap size={18} />
            </ActionIcon>
            <ActionIcon
                onClick={toggleLayerFilter}
                size="lg"
                radius="xl"
                variant="filled"
                color={showLayerFilter ? "blue" : "primary"}
                title="Capas"
            >
                <IconStackMiddle size={18} />
            </ActionIcon>
        </section>
    );

    const leftPanelComponent = (
        <LeftPanel
            showLeftPanel={showLeftPanel}
            leftPanelSection={leftPanelSection}
            selectedEstablishment={selectedEstablishment}
            isLoadingEstablishments={isLoadingEstablishments}
            searchParams={searchParams}
            codeSiep={codeSiep}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            MAPBOX_STYLES={MAPBOX_STYLES}
            selectedMapStyle={selectedMapStyle}
            setSelectedMapStyle={setSelectedMapStyle}
            showLayerFilter={showLayerFilter}
            setShowLayerFilter={setShowLayerFilter}
            selectedLayers={selectedLayers}
            setSelectedLayers={setSelectedLayers}
        />
    );

    const rightPanelComponent = (
        <RightPanel
            widgets={widgets}
            currentWidgetIndex={currentWidgetIndex}
            setCurrentWidgetIndex={setCurrentWidgetIndex}
            codeSiep={codeSiep}
        />
    );

    return (
		<React.Fragment>
            <MapEstablishmentList
                selectedEstablishmentSiep={codeSiep}
                onSelectEstablishment={handleSelectEstablishment}
                zoom={5}
                fullscreen={false}
                establishments={mapEstablishments}
                isLoadingEstablishments={isLoadingEstablishments}
                mapboxStyles={selectedMapStyle}
                leftPanel={leftPanelComponent}
                rightPanel={rightPanelComponent}
                actionButtons={actionButtonsComponent}
            />
		</React.Fragment>
	);
};

export default DashboardVariableRILes;
